import cn from 'classnames'
import type {ChangeEvent} from 'react'
import {useId, useState} from 'react'
import {clampNumber} from 'slices/RoiCalculator/utils'

export type NumberFieldProps = {
  label: string
  value: number
  min: number
  max: number
  step?: number
  prefix?: string
  hint?: string
  onChange: (value: number) => void
}

export const NumberField = ({label, value, min, max, step = 1, prefix, hint, onChange}: NumberFieldProps) => {
  const id = useId()
  const [draft, setDraft] = useState<string | null>(null)
  const displayedValue = draft !== null && (draft === '' || Number(draft) === value) ? draft : String(value)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDraft(event.target.value)
    onChange(clampNumber(Number(event.target.value), min, max))
  }

  const increment = () => onChange(clampNumber(value + step, min, max))

  const decrement = () => onChange(clampNumber(value - step, min, max))

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-16 leading-none text-moss-grey-550">
        {label}
      </label>
      <div
        className={cn(
          'flex items-center rounded-lg border border-moss-black/10 bg-moss-white',
          'focus-within:ring-2 focus-within:ring-green-dark',
        )}
      >
        {prefix && (
          <span aria-hidden="true" className="pl-4 text-16 leading-tight text-moss-black">
            {prefix}
          </span>
        )}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          value={displayedValue}
          onChange={handleChange}
          onBlur={() => setDraft(null)}
          className={cn(
            'w-full bg-transparent py-3 pl-4 pr-1 text-16 leading-tight text-moss-black focus:outline-none',
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            prefix && '!pl-1',
          )}
        />
        <div className="flex items-center gap-0.5 pr-2">
          <button
            type="button"
            onClick={decrement}
            aria-label={`Decrease ${label}`}
            className="flex size-7 items-center justify-center rounded bg-moss-grey-100 text-moss-grey-400 transition-colors hover:bg-moss-grey-200 hover:text-moss-black"
          >
            <span className="text-16 font-medium leading-none">−</span>
          </button>
          <button
            type="button"
            onClick={increment}
            aria-label={`Increase ${label}`}
            className="flex size-7 items-center justify-center rounded bg-moss-grey-100 text-moss-grey-400 transition-colors hover:bg-moss-grey-200 hover:text-moss-black"
          >
            <span className="text-16 font-medium leading-none">+</span>
          </button>
        </div>
      </div>
      {hint && <p className="text-12 leading-none text-moss-grey-550">{hint}</p>}
    </div>
  )
}
